import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Container } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { oauthService } from '../services/oauthService';

/**
 * OAuth Callback Page
 *
 * This page handles the redirect from Microsoft after user authentication.
 * It exchanges the authorization code for an access token and logs the user in.
 */
const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string>('');
  const [processing, setProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent multiple executions (React StrictMode causes double mounting)
      if (hasProcessed.current) {
        return;
      }
      hasProcessed.current = true;
      try {
        // Get authorization code and state from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check if Microsoft returned an error
        if (errorParam) {
          setError(errorDescription || 'Authentication was cancelled or failed');
          setProcessing(false);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setError('Invalid callback parameters. Please try logging in again.');
          setProcessing(false);
          return;
        }

        // Exchange code for token
        const response = await oauthService.handleCallback(code, state);

        // Store token and user info
        localStorage.setItem('token', response.access_token);
        localStorage.setItem('user', JSON.stringify(response.user));

        // Land each role on the page it can actually use: Servicio Técnico
        // only operates the on-prem product; ADMIN gets the BI dashboard.
        // The OAuth response carries a single `role`; the rest of the app
        // reads `roles[]` from localStorage, so accept either shape.
        const u = response.user as { role?: string; roles?: string[] } | undefined;
        const roles: string[] = u?.roles ?? (u?.role ? [u.role] : []);
        navigate(roles.includes('ADMIN') ? '/bi-dashboard' : '/onprem-clients');
      } catch (err: any) {
        console.error('OAuth callback error:', err);
        setError(err.message || 'Authentication failed. Please try again.');
        setProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {processing ? (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Completando inicio de sesión...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Procesando tu autenticación con Microsoft
            </Typography>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="body1" align="center" sx={{ mb: 2 }}>
              Por favor, intenta iniciar sesión nuevamente.
            </Typography>
            <Box
              component="a"
              href="/"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Volver al inicio de sesión
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default OAuthCallback;
